import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, TokenStore } from '../context/AuthContext';
import axios from 'axios';
import { Spinner } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthCallback = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    const sessionId = sessionIdMatch ? sessionIdMatch[1] : null;

    if (!sessionId) {
      setError('No session received from Google');
      return;
    }

    const exchangeSession = async () => {
      try {
        const { data } = await axios.post(`${API}/api/auth/google/callback`, {
          session_id: sessionId
        });

        // Use centralized token storage
        TokenStore.setTokens(data.access_token, data.refresh_token);

        window.history.replaceState(null, '', window.location.pathname);
        await checkAuth();
        navigate('/', { replace: true });
      } catch (e) {
        /* Surface as much detail as possible so the user / admin can
         * actually diagnose a failure instead of seeing a vague
         * "Authentication failed".  The most common production failures
         * are:
         *   - Session_id already consumed (e.g. duplicate dispatch / back)
         *   - Emergent demo OAuth backend timing out
         *   - Network error (no response object at all)
         */
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        const niceMessage =
          detail
            ? `${detail}`
            : status
              ? `Server returned ${status}. Please try signing in again.`
              : `Network error reaching the server. Check your connection and retry.`;
        // eslint-disable-next-line no-console
        console.error('[auth-callback] failure', { status, detail, error: e });
        setError(niceMessage);
      }
    };

    exchangeSession();
    // Intentionally run once on mount only — sessionId comes from URL hash
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-8">
        <div className="max-w-md w-full text-center">
          <div className="bg-[#141414]/80 backdrop-blur-xl border border-[#262626] rounded-2xl p-8 shadow-2xl shadow-black/50">
            <div className="w-16 h-16 rounded-2xl bg-[#EF4444]/10 flex items-center justify-center border border-[#EF4444]/20 mx-auto mb-6">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
            </div>
            <h2 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Sign In Failed</h2>
            <p className="text-[#A3A3A3] text-sm mb-6">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="w-full py-3 bg-gradient-to-r from-[#CD040B] to-[#FF4B51] hover:from-[#EB050D] hover:to-[#FF6B6F] text-white rounded-xl font-semibold transition-all duration-300"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="text-center">
        <Spinner size={40} className="animate-spin text-[#CD040B] mx-auto mb-4" />
        <p className="text-[#A3A3A3] text-sm">Signing you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
